import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { showSystemToast } from "../utils/system_toast"
import { renderGroupChips, setChipMember, requestGroupMembership } from "../utils/contact_group_chips"

// "Edit connections" modal on the network index: row kebabs dispatch a
// contact-connections:open document event with the contact's id/name/group
// slugs. Group chips toggle membership instantly; the relationship form posts
// to the same endpoint as the tree pages. Closing after any change reloads
// the list frame so row chips and filter membership stay in sync.
export default class extends Controller {
    static targets = ["modalOverlay", "modalForm", "modalName", "modalError", "modalGroups"]
    static values = { contactsBase: String, groups: Array }

    open(event) {
        let detail = event.detail || {}
        if (!detail.contactId) return
        this._contactId = String(detail.contactId)
        this._name = detail.name || "This contact"
        this._slugs = new Set(detail.groupSlugs || [])
        this._changed = false
        this.modalNameTarget.textContent = this._name
        this.modalErrorTarget.hidden = true
        renderGroupChips(this.modalGroupsTarget, this.groupsValue, (group) => this._slugs.has(group.slug), (group, chip) => this._toggleGroup(group, chip))
        let form = this.modalFormTarget
        form.reset()
        form.setAttribute("data-contact-picker-exclude-id-value", this._contactId)
        let hidden = form.querySelector("[data-contact-picker-target='id']")
        if (hidden) hidden.value = ""
        let submit = form.querySelector("[data-contact-picker-target='submit']")
        if (submit) submit.disabled = true
        this.modalOverlayTarget.hidden = false
        form.querySelector("[data-contact-picker-target='input']")?.focus()
    }

    closeModal(event) {
        event?.preventDefault()
        this.modalOverlayTarget.hidden = true
        if (this._changed) this._refreshList()
        this._changed = false
        this._contactId = null
    }

    overlayClick(event) {
        if (event.target === this.modalOverlayTarget) this.closeModal()
    }

    submitRelationship(event) {
        event.preventDefault()
        if (!this._contactId) return
        let form = this.modalFormTarget
        let body = new FormData(form)
        if (!body.get("relationship[related_contact_id]")) return
        let submit = form.querySelector("[data-contact-picker-target='submit']")
        if (submit) submit.disabled = true
        fetch(`${this.contactsBaseValue}/${this._contactId}/relationships`, {
            method: "POST",
            headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body
        })
            .then((response) => response.json().then((data) => ({ response, data })))
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not add that relationship.")
                this._changed = true
                showSystemToast(data.notice || "Relationship added.")
                this.closeModal()
            })
            .catch((error) => {
                this.modalErrorTarget.textContent = error.message || "Could not add that relationship."
                this.modalErrorTarget.hidden = false
                if (submit) submit.disabled = false
            })
    }

    _toggleGroup(group, chip) {
        let member = this._slugs.has(group.slug)
        chip.disabled = true
        requestGroupMembership(this.contactsBaseValue, this._contactId, group.slug, member ? "DELETE" : "POST")
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not update group")
                if (data.member) this._slugs.add(group.slug)
                else this._slugs.delete(group.slug)
                this._changed = true
                setChipMember(chip, data.member)
                showSystemToast(data.member ? `Added ${this._name} to ${group.name}.` : `Removed ${this._name} from ${group.name}.`)
            })
            .catch((error) => showSystemToast(error.message || "Could not update group.", { level: "danger" }))
            .finally(() => { chip.disabled = false })
    }

    _refreshList() {
        let frame = document.querySelector("turbo-frame#network_list")
        if (frame?.src) frame.reload()
        else window.location.reload()
    }
}
